import { setThemeUserProperties } from 'lib/analytics';
import { useTenantProperties } from 'queries/hooks/useTenantProperties';
import { useEffect } from 'react';
import { useForcedTheme } from './useForcedTheme';

export enum ThemeOptions {
    'light' = 'light',
    'dark' = 'dark',
    'system' = 'system'
}

const themeLocalStorageRef = 'SqUpTheme';
export const systemThemeDataAttribute = 'data-theme';

export const getSystemTheme = () =>
    window?.matchMedia?.('(prefers-color-scheme: light)')?.matches ? ThemeOptions.light : ThemeOptions.dark;

export const useTheme = () => {
    const forcedTheme = useForcedTheme();

    const { data: tenantTheme, isPlaceholderData } = useTenantProperties({
        select: (data): ThemeOptions => (data?.theme as ThemeOptions) ?? ThemeOptions.system,
        placeholderData: {
            theme: (localStorage.getItem(themeLocalStorageRef) as ThemeOptions) ?? ThemeOptions.system
        }
    });

    const theme = forcedTheme ?? tenantTheme;

    useEffect(() => {
        if (!theme) {
            return;
        }

        // Persist theme in local storage for initial colours on app load (if not being forced by URL).
        if (!forcedTheme) {
            localStorage.setItem(themeLocalStorageRef, theme);
        }

        const effectiveTheme = theme !== ThemeOptions.system ? theme : getSystemTheme();

        /**
         * A theme forced by the URL (embeds, open access) isn't the user's own choice, so it must not
         * overwrite the theme properties recorded for them. Nor is the local storage placeholder, which
         * can be stale until the tenant properties load.
         */
        const shouldRecordTheme = !forcedTheme && !isPlaceholderData;

        if (shouldRecordTheme) {
            setThemeUserProperties(theme as ThemeOptions, effectiveTheme as ThemeOptions);
        }

        document.documentElement.setAttribute(systemThemeDataAttribute, effectiveTheme);
        document.documentElement.style.colorScheme = effectiveTheme === ThemeOptions.light ? 'light' : 'dark';

        // Set Amplitude guide theme
        // See https://amplitude.com/docs/guides-and-surveys/sdk#manage-themes
        const engagement = (window as any).engagement;
        engagement?.setThemeMode(effectiveTheme === ThemeOptions.light ? 'lightMode' : 'darkMode');

        if (theme !== ThemeOptions.system) {
            return;
        }

        // If the user has selected to follow the system theme we attach an event listener and update as it changes.
        const handleSystemThemeChange = (event: MediaQueryListEvent) => {
            const systemTheme = event.matches ? ThemeOptions.light : ThemeOptions.dark;

            document.documentElement.setAttribute(systemThemeDataAttribute, systemTheme);
            document.documentElement.style.colorScheme = event.matches ? 'light' : 'dark';

            if (shouldRecordTheme) {
                setThemeUserProperties(ThemeOptions.system, systemTheme);
            }
        };

        const systemThemeWatcher = window.matchMedia('(prefers-color-scheme: light)');
        systemThemeWatcher.addEventListener('change', handleSystemThemeChange);

        return () => {
            systemThemeWatcher.removeEventListener('change', handleSystemThemeChange);
        };
    }, [theme, forcedTheme, isPlaceholderData]);

    // React query placeholder data forces theme to always be defined
    return !theme ? ThemeOptions.system : (theme as ThemeOptions);
};
